import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Form, Input, Space, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { SearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { ReadonlyRangePicker } from '../../components/common/DatePickerControls'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListCard,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import { PageLayout } from '../../components/layout'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import { localeService } from '../../services/localeService'
import { getOperationLogs } from '../../services/operationLogService'
import type {
    OperationLog as OperationLogRecord,
    OperationLogQueryParams,
} from '../../types/operationLog'
import { getLocalizedApiErrorMessage } from '../../utils/apiErrorI18n'
import { normalizeSearchText } from '../../utils/searchText'

const OPERATION_LOG_I18N_PREFIX = 'admin:operationLog'
const OPERATION_LOG_STATUS_FIELD_PATTERN = /(【(?:状态|绑定状态)】)([^，]*)/g
const OPERATION_LOG_STATUS_TOKEN_PATTERN = /[A-Za-z][A-Za-z0-9_]*/g
const OPERATION_LOG_COLOR_HEX_PATTERN = /#[0-9A-Fa-f]{6}\b/g
const DEVICE_UPGRADE_STATUS_KEYS = new Set([
    'bound',
    'unbound',
    'open',
    'resolved',
    'ignored',
    'draft',
    'queued',
    'building',
    'built',
    'uploaded',
    'pending',
    'running',
    'success',
    'partial_success',
    'failed',
    'sync_failed',
    'disabled',
    'canceled',
])
const ARTIFACT_AVAILABILITY_ACTIONS = new Set([
    '启用升级包',
    '停用升级包',
])

const OPERATION_LOG_COLOR_VALUE_STYLE: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    margin: '0 2px',
    verticalAlign: 'middle',
}

const OPERATION_LOG_COLOR_SWATCH_STYLE: React.CSSProperties = {
    width: 12,
    height: 12,
    borderRadius: 3,
    border: '1px solid rgba(15, 23, 42, 0.2)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.45)',
    flex: '0 0 auto',
}

interface OperationLogFilters {
    timeRange?: [Dayjs, Dayjs] | null
    module_keyword?: string
    action_keyword?: string
    operator_keyword?: string
    keyword?: string
}

const DEFAULT_PAGE_SIZE = 10

const formatOperationLogLoadError = (
    error: unknown,
    fallback: string,
    t: TFunction
): string => getLocalizedApiErrorMessage(error, fallback, t)

const normalizeText = (value?: string) => {
    const normalized = normalizeSearchText(value)
    return normalized || undefined
}

const formatOtaDisplayText = (value: string): string =>
    value
        .replace(/(^|[^.])mender\s+server/gi, (_match, prefix: string) => `${prefix}OTA 服务`)
        .replace(/(^|[^.])mender/gi, (_match, prefix: string) => `${prefix}OTA`)

const formatOperationLogRecord = (
    value: string,
    operationAction: string,
    t: TFunction
): string =>
    formatOtaDisplayText(value).replace(
        OPERATION_LOG_STATUS_FIELD_PATTERN,
        (_match, label: string, fieldValue: string) =>
            `${label}${fieldValue.replace(
                OPERATION_LOG_STATUS_TOKEN_PATTERN,
                (status) => {
                    if (!DEVICE_UPGRADE_STATUS_KEYS.has(status)) {
                        return status
                    }
                    if (
                        ARTIFACT_AVAILABILITY_ACTIONS.has(operationAction) &&
                        status !== 'disabled'
                    ) {
                        return t(
                            `${OPERATION_LOG_I18N_PREFIX}.statuses.artifactEnabled`,
                            { defaultValue: '已启用' }
                        )
                    }
                    return t(`admin:deviceUpgrade.statuses.${status}`, {
                        defaultValue: status,
                    })
                }
            )}`
    )

const renderOperationLogRecordText = (value: string): React.ReactNode => {
    const matches = Array.from(value.matchAll(OPERATION_LOG_COLOR_HEX_PATTERN))
    if (matches.length === 0) {
        return value
    }

    const parts: React.ReactNode[] = []
    let cursor = 0

    matches.forEach((match, index) => {
        const color = match[0]
        const matchIndex = match.index ?? 0
        if (matchIndex > cursor) {
            parts.push(value.slice(cursor, matchIndex))
        }
        parts.push(
            <span key={`${color}-${index}`} style={OPERATION_LOG_COLOR_VALUE_STYLE}>
                <span
                    aria-label={`颜色 ${color}`}
                    data-testid="operation-log-color-swatch"
                    style={{
                        ...OPERATION_LOG_COLOR_SWATCH_STYLE,
                        backgroundColor: color,
                    }}
                />
                {color}
            </span>
        )
        cursor = matchIndex + color.length
    })

    if (cursor < value.length) {
        parts.push(value.slice(cursor))
    }

    return <>{parts}</>
}

const renderOperationLogRecordCell = (
    value: string,
    record: OperationLogRecord,
    t: TFunction
) => {
    const formattedRecord = formatOperationLogRecord(
        value,
        record.operation_action,
        t
    )

    return (
        <Tooltip
            title={
                formattedRecord
                    ? renderOperationLogRecordText(formattedRecord)
                    : undefined
            }
            placement="topLeft"
            overlayStyle={{ maxWidth: 720 }}
        >
            {renderPaginatedListText(
                renderOperationLogRecordText(formattedRecord)
            )}
        </Tooltip>
    )
}

const OperationLog: React.FC = () => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['admin', 'navigation'])
    const [form] = Form.useForm<OperationLogFilters>()
    const [logs, setLogs] = useState<OperationLogRecord[]>([])
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const buildParams = useCallback(
        (
            values: OperationLogFilters,
            page: number,
            size: number
        ): OperationLogQueryParams => ({
            start_time: values.timeRange?.[0]?.startOf('day').toISOString(),
            end_time: values.timeRange?.[1]?.endOf('day').toISOString(),
            module_keyword: normalizeText(values.module_keyword),
            action_keyword: normalizeText(values.action_keyword),
            operator_keyword: normalizeText(values.operator_keyword),
            keyword: normalizeText(values.keyword),
            page,
            page_size: size,
        }),
        []
    )

    const loadLogs = useCallback(
        async (page: number, size: number) => {
            setLoading(true)
            try {
                const values = form.getFieldsValue()
                const data = await getOperationLogs(
                    buildParams(values, page, size)
                )
                setLogs(data.items)
                setTotal(data.total)
                setCurrentPage(data.page)
                setPageSize(data.page_size)
            } catch (error) {
                message.error(
                    formatOperationLogLoadError(
                        error,
                        t(`${OPERATION_LOG_I18N_PREFIX}.messages.loadFailed`),
                        t
                    )
                )
            } finally {
                setLoading(false)
            }
        },
        [buildParams, form, message, t]
    )

    useEffect(() => {
        form.setFieldsValue({
            timeRange: [dayjs().subtract(30, 'days'), dayjs()],
        })
        void loadLogs(1, DEFAULT_PAGE_SIZE)
    }, [form, loadLogs])

    const handleSearch = async () => {
        await loadLogs(1, pageSize)
    }

    const handleReset = async () => {
        form.resetFields()
        form.setFieldsValue({
            timeRange: [dayjs().subtract(30, 'days'), dayjs()],
        })
        await loadLogs(1, pageSize)
    }

    const columns: ColumnsType<OperationLogRecord> = [
        {
            title: t(`${OPERATION_LOG_I18N_PREFIX}.columns.operationTime`),
            dataIndex: 'operation_time',
            key: 'operation_time',
            width: 180,
            render: (value: string) => localeService.formatDateTime(value),
        },
        {
            title: t(`${OPERATION_LOG_I18N_PREFIX}.columns.operatorName`),
            dataIndex: 'operator_name',
            key: 'operator_name',
            width: 140,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t(`${OPERATION_LOG_I18N_PREFIX}.columns.operatorAccount`),
            dataIndex: 'operator_account',
            key: 'operator_account',
            width: 140,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t(`${OPERATION_LOG_I18N_PREFIX}.columns.operationModule`),
            dataIndex: 'operation_module',
            key: 'operation_module',
            width: 160,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t(`${OPERATION_LOG_I18N_PREFIX}.columns.operationAction`),
            dataIndex: 'operation_action',
            key: 'operation_action',
            width: 120,
            ellipsis: true,
            render: renderPaginatedListText,
        },
        {
            title: t(`${OPERATION_LOG_I18N_PREFIX}.columns.operationRecord`),
            dataIndex: 'operation_record',
            key: 'operation_record',
            ellipsis: { showTitle: false },
            render: (value: string, record) =>
                renderOperationLogRecordCell(value, record, t),
        },
    ]

    return (
        <PageLayout
            title={t(`${OPERATION_LOG_I18N_PREFIX}.title`)}
            breadcrumb={[
                {
                    title: t('navigation:breadcrumb.systemManagement', {
                        defaultValue: t(
                            `${OPERATION_LOG_I18N_PREFIX}.breadcrumbSystemManagement`
                        ),
                    }),
                },
                {
                    title: t('navigation:breadcrumb.operationLog', {
                        defaultValue: t(`${OPERATION_LOG_I18N_PREFIX}.title`),
                    }),
                },
            ]}
        >
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Form form={form} layout="vertical">
                    <FilterBar>
                        <FilterField
                            label={t(
                                `${OPERATION_LOG_I18N_PREFIX}.filters.timeRange`
                            )}
                        >
                            <Form.Item name="timeRange" noStyle>
                                <ReadonlyRangePicker />
                            </Form.Item>
                        </FilterField>
                        <FilterField
                            label={t(
                                `${OPERATION_LOG_I18N_PREFIX}.filters.module`
                            )}
                        >
                            <Form.Item name="module_keyword" noStyle>
                                <Input
                                    allowClear
                                    placeholder={t(
                                        `${OPERATION_LOG_I18N_PREFIX}.placeholders.module`
                                    )}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterField
                            label={t(
                                `${OPERATION_LOG_I18N_PREFIX}.filters.action`
                            )}
                        >
                            <Form.Item name="action_keyword" noStyle>
                                <Input
                                    allowClear
                                    placeholder={t(
                                        `${OPERATION_LOG_I18N_PREFIX}.placeholders.action`
                                    )}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterField
                            label={t(
                                `${OPERATION_LOG_I18N_PREFIX}.filters.operator`
                            )}
                        >
                            <Form.Item name="operator_keyword" noStyle>
                                <Input
                                    allowClear
                                    placeholder={t(
                                        `${OPERATION_LOG_I18N_PREFIX}.placeholders.operator`
                                    )}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterField
                            label={t(
                                `${OPERATION_LOG_I18N_PREFIX}.filters.keyword`
                            )}
                        >
                            <Form.Item name="keyword" noStyle>
                                <Input
                                    allowClear
                                    placeholder={t(
                                        `${OPERATION_LOG_I18N_PREFIX}.placeholders.keyword`
                                    )}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterActions>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={() => void handleSearch()}
                            >
                                {t(
                                    `${OPERATION_LOG_I18N_PREFIX}.actions.search`
                                )}
                            </Button>
                            <Button onClick={() => void handleReset()}>
                                {t(
                                    `${OPERATION_LOG_I18N_PREFIX}.actions.reset`
                                )}
                            </Button>
                        </FilterActions>
                    </FilterBar>
                </Form>

                <PaginatedListCard>
                    <Table
                        rowKey="id"
                        loading={loading}
                        dataSource={logs}
                        columns={columns}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        pagination={buildPaginatedListPagination({
                            current: currentPage,
                            pageSize,
                            total,
                            showTotal: (count) =>
                                t(`${OPERATION_LOG_I18N_PREFIX}.total`, {
                                    total: count,
                                }),
                            onChange: (page, size) => {
                                void loadLogs(page, size)
                            },
                        })}
                        scroll={{ x: 1050 }}
                    />
                </PaginatedListCard>
            </Space>
        </PageLayout>
    )
}

export default OperationLog
